import DOMPurify from 'dompurify';
import { DisplayLanguage, Gender, Graduation, LanguageLevel, Location, MatchStatus, MediationStatus, Notifications, Salutation, Title, TwoFactorAuthType, UserType } from './enums.mjs';
import { useContext } from 'react';
import { LoginContext } from './App';
import moment, { locale } from 'moment';
import { locationStartPagesSettings, maxUsernameLength } from './globals_common.mjs';
import { system } from './globals.mjs';

export var csrfToken = null;

export function setCsrfToken(newToken) {
    csrfToken = newToken;
}

export function fetchCsrfToken() {
    // Retrieve a CSRF token from the Django webserver
    return fetch("/user/get_csrf").then((response) => {
        if(response.ok) {
            setCsrfToken(getCookie('csrftoken'));
            // console.log("Token extracted: " + csrfToken);
            return response;
        }
        
        throw new Error("Error response: " + response);
    });
}

export function postWithCsrf(url, data, stateContext, tryCount=0) {
    const maxTries = 2;
    tryCount += 1;

    if(tryCount === 1 && stateContext.fetching.current) {
        // Do not return a rejecting promise as this could lead to a .catch() block beeing executing and afterwards a successfully query arrives and the .then()
        // will be executed (happened to Tommy once while posting to set_data).
        // return Promise.reject(new Error("Already fetching"));

        // Immediately stop execution here by throwing an exception
        throw new Error("Already fetching");
    }

    stateContext.fetching.current = true;

    return fetch(url, {
        method: "POST",
        headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': csrfToken,  // Include the CSRF token in the request header
            'X-User-Interaction': 'x'
        },
        body: data
    }).then((response) => {
        if(response.status == 403 && tryCount < maxTries)
        {
            console.log("Received status " + response.status + ". Retrying with new CSRF token... (" + (tryCount + 1) + "/" + maxTries + ")");

            return fetchCsrfToken().then(() => {
                return postWithCsrf(url, data, stateContext, tryCount);
            });
        }

        return response;
    }).finally(() => {
        if(tryCount === 1) {    
            stateContext.fetching.current = false;
        }
    });
}

function getCookie(name) {
    let cookieValue = null;

    // console.log("Cookie: " + (document.cookie.length));

    if (document.cookie && document.cookie !== '') {
        const cookies = document.cookie.split(';');

        // console.log("Cookies len: " + cookies.length);

        for (let i = 0; i < cookies.length; i++) {
            const cookie = cookies[i].trim();
            // Does this cookie string begin with the name we want?

            // console.log(cookie.substring(0, name.length + 1));

            if (cookie.substring(0, name.length + 1) === (name + '=')) {
                cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
                break;
            }
        }
    }
    return cookieValue;
}

// Do check for exceptions like when using postWithCsrf() directly!
export function loginFunc(email, password, two_factor_code, stateContext, loginContext, url,
        recaptcha_token=null, overlay=null, locale=null, displayErrorObj=null) {
    return postWithCsrf(
        url,
        JSON.stringify({email: email, password: password, two_factor_code: two_factor_code,
                recaptcha_token: recaptcha_token}),
        stateContext
    ).then((response) => {
        if(response.ok) {
            return fetchCsrfToken().then(csrfResponse => {
                if(csrfResponse.ok) {
                    return response.json();
                }
                
                throw new Error("Response code: " + response.status);
            });
        }

        if(response.status == 527) {
            return response.text().then(() => {
                overlay.setErrorPopupInfo({"title": locale.msgs.BOT_PROTECTION_ERROR, "text": locale.msgs.BOT_PROTECTION_ERROR_DESCR});
                overlay.setErrorPopupDisplayed(true);

                displayErrorObj.displayError = false;
                throw new Error("Response code: " + response.status);
            });
        }
        else {
            throw new Error("Response code: " + response.status);
        }

        throw new Error("Response code: " + response.status);
    }).then((json) => {
        setLoginStatesFromJson(json, loginContext);
        return json;
    });
}

// Do check for exceptions like when using postWithCsrf() directly!
export function logoutConsumer(state, login, consumer) {
    return logout(state, login).then((response) => {
        resetLoginStatesConsumer(login, consumer);
        return response;
    })
}

// Do check for exceptions like when using postWithCsrf() directly!
export function logout(state, login) {
    return postWithCsrf("/user/logout", {}, state).then(response => {
        if(response.ok) {
            resetLoginStates(login);
            return response;
        }

        throw new Error("Response code: " + response.status);
    });
}

export function setLoginStatesFromJsonConsumer(json, consumer) {
    consumer.setMediationStatus(json.mediation_status);
    consumer.setDeclined(json.declined);
    consumer.setInactivityQuery(json.inactivity_status);
    consumer.setNewsletter(json.newsletter);
    consumer.setDisplayLanguage(json.display_language);
    consumer.setUserLocation(json.location);

    if(json.user_type === UserType.FAMILY) {
        consumer.setProtectionFeeSkipped(json.protection_fee_skipped);
        consumer.setMediationFeeSkipped(json.mediation_fee_skipped);
    }
}

export function setLoginStatesFromJson(json, login) {
    login.setLoggedIn(true);

    login.setMailConfirmed(json.email_confirmed);
    login.setUsername(json.username);
    login.setTwoFactorAuthEnabled(json.two_factor_auth_enabled);

    login.setTwoFactorAuthType(json.two_factor_auth_type);
    login.setUserType(json.user_type);
    login.setNotifications(json.notifications);
    login.setTwoFactorAuthMobileNo(json.two_factor_auth_mobile_no);
}

export function resetLoginStates(login) {
    login.setLoggedIn(false);

    login.setMailConfirmed(false);
    login.setUsername("");
    login.setTwoFactorAuthEnabled(false);

    login.setPassword("");
    login.setTwoFactorAuthType(TwoFactorAuthType.NONE);
    login.setUserType("");
    login.setTwoFactorAuthMobileNo(null);
}

export function resetLoginStatesConsumer(login, consumer) {
    consumer.setMediationStatus("");
    consumer.setDeclined(null);
    consumer.setInactivityQuery(null);
    consumer.setNewsletter(false);
    consumer.setDisplayLanguage(DisplayLanguage.NONE);
    consumer.setUserLocation(Location.NONE);

    resetLoginStates(login);
}

export function get_translation_objects(name, obj, locale) {
    let result = {};
    let resultInverse = {};

    for(let [key, val] of Object.entries(obj)) {
        if(key != "NONE") {
            const localeKey = name.toUpperCase() + "_" + key.toUpperCase();
            const transVal = locale.msgs[localeKey];

            if(transVal != undefined) {
                result[val] = transVal;
                resultInverse[transVal] = val;
            }
        }
        else {
            result[val] = "";
            resultInverse[""] = val;
        }
    }

    return [result, resultInverse];
}

export function sanitizeLocaleMessages(messages) {
    const cleanMessages = {};

    for(let [k, v] of Object.entries(messages)) {
        cleanMessages[k] = DOMPurify.sanitize(v);
    }

    return cleanMessages;
}

export function sanitizeStr(str) {
    return DOMPurify.sanitize(str);
}

export function uploadFiles(filesToUpload, filesToDelete, shareInfo, url, progressCb, successCb, errorCb, tryCount=0, addInfo=null) {
    const maxTries = 2;
    tryCount += 1;

    // Prepare FormData object to send the file in a POST request
    const formData = new FormData();

    for(let f of filesToUpload) {
        formData.append('upload[]', f.file, f.file.name);
    }

    formData.append('uploadInfo[]', JSON.stringify(filesToUpload));

    if(filesToDelete) {
        formData.append('delete[]', JSON.stringify(filesToDelete));
    }

    if(shareInfo) {
        formData.append('shareInfo[]', JSON.stringify(shareInfo));
    }

    if(addInfo) {
        for(const i of addInfo) {
            formData.append(i[0], i[1]);
        }
    }
    
    // Create a new XMLHttpRequest
    const xhr = new XMLHttpRequest();

    function abort() {
        xhr.abort();
    }

    // Abort after 60 seconds as I saw cases where no internal timeout of XMLHttpRequest happened
    setTimeout(abort, 60000);
    
    // Set up the request
    xhr.open('POST', url, true);
    xhr.setRequestHeader("X-CSRFToken", csrfToken);

    // Track the upload progress
    xhr.upload.onprogress = (event) => {  
        if (event.lengthComputable) {
            const percentComplete = (event.loaded / event.total) * 100;
            progressCb(Math.round(percentComplete).toString());
        }
    };
    
    // Set up a handler for when the request finishes
    xhr.onload = () => {
        if (xhr.status === 200) {
            successCb(JSON.parse(xhr.response));
        } else {
            if(xhr.status  == 403 && tryCount < maxTries)
            {
                console.log("Received status " + xhr.status + ". Retrying with new CSRF token... (" + (tryCount + 1) + "/" + maxTries + ")");
        
                return fetchCsrfToken().then(() => {
                    return uploadFiles(filesToUpload, filesToDelete, shareInfo, url, progressCb, successCb, errorCb, tryCount);
                });
            }

            errorCb();
        }
    };

    xhr.onerror = () => {
        clearTimeout(abort, 5000);
        errorCb();
    };

    xhr.onabort = () => {
        clearTimeout(abort, 5000);
        errorCb();
    };
    
    // Send the FormData with the file
    xhr.send(formData);
}

export function convertIsoToLocalIsoWithoutTimezone(isoUtcString) {  
  // Parse the ISO UTC string into a Date object  
  const date = new Date(isoUtcString);  
    
  // Extract components from the Date object  
  const year = date.getFullYear();  
  const month = (date.getMonth() + 1).toString().padStart(2, '0'); // getMonth() returns 0-11  
  const day = date.getDate().toString().padStart(2, '0');  
  const hours = date.getHours().toString().padStart(2, '0');  
  const minutes = date.getMinutes().toString().padStart(2, '0');  
  const seconds = date.getSeconds().toString().padStart(2, '0');  
  const milliseconds = date.getMilliseconds().toString().padStart(3, '0');  
    
  // Construct a new ISO string with local time components  
  const isoLocalString = `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${milliseconds}`;  
    
  return isoLocalString;  
}

function getMediationStatusIdx(mediationStatus) {
    const entries = Object.entries(MediationStatus);

    for(let i = 0; i < entries.length; i++) {
        const entry = entries[i];

        if(entry[1] === mediationStatus) {
            return i;
        }
    }

    return -1;
}

function getMatchStatusIdx(matchStatus) {
    const entries = Object.entries(MatchStatus);

    for(let i = 0; i < entries.length; i++) {
        const entry = entries[i];

        if(entry[1] === matchStatus) {
            return i;
        }
    }

    return -1;
}

// Returns true if mediationStatus1 is higher or equal than mediationStatus2
export function compareMediationStatus(mediationStatus1, mediationStatus2) {
    const idx1 = getMediationStatusIdx(mediationStatus1);
    const idx2 = getMediationStatusIdx(mediationStatus2);

    if(idx1 >= idx2) {
        return true;
    }

    return false;
}

// Returns true if mediationStatus1 is higher than mediationStatus2
export function compareMediationStatus2(mediationStatus1, mediationStatus2) {
    const idx1 = getMediationStatusIdx(mediationStatus1);
    const idx2 = getMediationStatusIdx(mediationStatus2);

    if(idx1 > idx2) {
        return true;
    }

    return false;
}

// Returns true if matchStatus1 is higher or equal than matchStatus2
export function compareMatchStatus(matchStatus1, matchStatus2) {
    const idx1 = getMatchStatusIdx(matchStatus1);
    const idx2 = getMatchStatusIdx(matchStatus2);

    if(idx1 >= idx2) {
        return true;
    }

    return false;
}

export function getUserIdStr(shortUser) {
    let idStr = "";

    if(shortUser.user_type === UserType.FAMILY) {
        idStr = "F" + String(shortUser.id);;
    }
    else if(shortUser.user_type === UserType.NANNY) {
        idStr = "M" + String(shortUser.id);
    }

    return idStr;
}

export function getUserStr(shortUser, locale) {
    let name = shortUser.email;

    const nameAvailable = shortUser.first_name && shortUser.last_name;
    
    if(shortUser.user_type === UserType.NANNY) {
        if(nameAvailable) {
            name = shortUser.first_name + " " + shortUser.last_name;
        }
    }
    else if(shortUser.user_type === UserType.FAMILY) {
        if(nameAvailable) {
            name = shortUser.first_name + " " + shortUser.last_name;
        }
    }

    // FRANCHISE START
    // Pad with zeroes
    /*if(idStr.length < 6) {
        const noOfZeroesToAdd = 6 - idStr.length;

        for(let i = 0; i < noOfZeroesToAdd; i++) {
            idStr = "0" + idStr;
        }
    }*/
    // FRANCHISE END

    const propName = "LOCATION_ABBR_" + shortUser.location.toUpperCase();
    const abbr = locale.msgs[propName];

    return name + " (" + getUserIdStr(shortUser) +
        ", " + abbr +
        ", " + shortUser.voting_user_category.toUpperCase() + (getMediationStatusIdx(shortUser.mediation_status) + 1) + ")";
}

export function getUserStr2(user, locale) {
    return getUserStr(Object.assign(user.care_data ? {first_name: user.care_data.first_name, last_name: user.care_data.last_name} : {}, user));
}

export function remToPixels(rem) {  
  // Get the font-size of the root element (html)  
  const fontSize = getComputedStyle(document.documentElement).fontSize;  
    
  // Parse the font-size back to a float (it's returned as a string, e.g., '16px')  
  const fontSizeInPixels = parseFloat(fontSize);  
    
  // Convert the rem value to float and multiply by root element's font-size  
  const pixels = parseFloat(rem) * fontSizeInPixels;  
    
  return pixels;  
}

export function getDateStr(isoStr, locale) {
    if(!isoStr) {
        return locale.msgs.UNKNOWN_DATE;
    }
    
    return (new Date(isoStr).toLocaleString(undefined, {year: "numeric", month: "2-digit", day: "2-digit"}));
}

export function getDateTimeStr(isoStr) {
    return moment(isoStr).format("DD.MM.YYYY, HH:mm") + " Uhr";
}

export function getAgeStr(ageNo, locale) {
    if(ageNo == 1) {
        return locale.msgs.YEAR.replace("${{num}}", "1");
    }
    else {
        return locale.msgs.YEARS.replace("${{num}}", ageNo.toString())
    }
}

export function getNoOfMatchSteps() {
    return 5;
}

export function getMatchOverviewDoneArr(user) {
    let doneArr = [false, false, false, false, false];

    if(user.declined) {
    }
    else if(user.match) {
        if(user.match.status === MatchStatus.NONE) {
            doneArr = [true, false, false,false, false];
        }
        else if(user.match.status === MatchStatus.SENT_TO_MN) {
            doneArr = [true, false, false, false, false];
        }
        else if(user.match.status === MatchStatus.MN_DECLINED) {
            doneArr = [true, false, false, false, false];
        }
        else if(user.match.status === MatchStatus.MN_CONFIRMED) {
            doneArr = [true, true, false, false, false];
        }
        else if(user.match.status === MatchStatus.SENT_TO_FAM) {
            doneArr = [true, true, false, false, false];
        }
        else if(user.match.status === MatchStatus.FAM_DECLINED) {
            doneArr = [true, true, false, false, false];
        }
        else if(user.match.status === MatchStatus.FAM_CONFIRMED) {
            doneArr = [true, true, true, false, false];
        }
        else if(user.match.status === MatchStatus.FINAL_CONFIRMATION) {
            doneArr = [true, true, true, true, false];
        }
        else if(user.match.status === MatchStatus.DONE) {
            doneArr = [true, true, true, true, true];
        }
    }
    else if(user.mediation_status === MediationStatus.FIND_MATCH) {
        if(!isMatchSearchCompleted(user)) {
            doneArr = [false, false, false, false, false];
        }
        else {
            doneArr = [true, false, false, false, false];
        }
    }

    return doneArr;
}

export function getCurMatchStep(user) {
    const doneArr = getMatchOverviewDoneArr(user);

    let step = 0;

    for(const v of doneArr) {
        if(v) {
            step++;
        }
        else {
            break;
        }
    }

    return step;
}

// THIS METHOD SHOULD ONLY BE USED FOR MANAGEMENT
// Input e.g.: "/users/data"
// Output e.g.: "/users"
export function getBasePath(locationPathname) {
    return "/" + locationPathname.split("/")[1];
}

// Input e.g.: "/users/data"
// Output e.g.: "/users"
export function getBasePathTrailing(locationPathname) {
    return "/" + locationPathname.split("/")[1] + "/";
}

// Use isTabActive instead!
// export function getBasePathFromUserType(userType) {
//     if(userType === UserType.NANNY) {
//         return "/nannies";
//     }
//     else if(userType === UserType.FAMILY) {
//         return "/families/";
//     }
//     else {
//         return "/users";
//     }
// }

export function isTabActive(tabBasepath, location) {

    if(tabBasepath === getBasePath(location.pathname)) {
        return true;
    }

    return false;
}

function isMatchSearchCompleted(user) {
    if(user.earmarked_matches.length === 0) {
        return false;
    }

    for(let e of user.earmarked_matches) {
        if(e.match_user_id === -1 && e.mediation_status === MediationStatus.FIND_MATCH) {
            return true;
        }
    }
}

export function checkEmail(email) {
    if(!email) {
        return false;
    }

    if(email === "") {
        return false;
    }

    if(email.length > maxUsernameLength) {
        return false;
    }

    if(!email.includes("@")){
        return false;
    }

    if(!email.includes(".")){
        return false;
    }

    return true;
}

export function get_translated_notifications(notifications, locale) {
    const [notificationsTransList, notificationsTransListInverse] = get_translation_objects("notifications", Notifications, locale);
    return notificationsTransList[notifications];
}

export function checkMinMaxDate(date, props, locale) {
    if(props.mustBeInFuture) {
        const nowUtcMs = (new Date()).getTime();
        const utcMs = date.getTime();

        if(utcMs <= nowUtcMs) {
            return props.time ? locale.msgs.DATETIME_MUST_BE_IN_FUTURE : locale.msgs.DATE_MUST_BE_IN_FUTURE;
        }
    }

    if(props.mustBeInPast) {
        const nowUtcMs = (new Date()).getTime();
        const utcMs = date.getTime();

        if(utcMs >= nowUtcMs) {
            return props.time ? locale.msgs.DATETIME_MUST_BE_IN_PAST : locale.msgs.DATE_MUST_BE_IN_PAST;
        }
    }

    if(props.legalAge) {
        let momentDate = moment(new Date());
        momentDate = momentDate.clone().add(-18, "years");

        const pastUtcMs = momentDate.toDate().getTime();
        const utcMs = date.getTime();

        if(utcMs >= pastUtcMs) {
           return locale.msgs.LEGAL_AGE_REQUIRED;
        }
    }

    if(props.maxDaysInFuture) {
        let momentDate = moment(new Date());
        momentDate = momentDate.clone().add(props.maxDaysInFuture, "days");

        const futureUtcMs = momentDate.toDate().getTime();
        const utcMs = date.getTime();

        if(utcMs >= futureUtcMs) {
           return props.time ? locale.msgs.DATETIME_TOO_FAR_IN_FUTURE : locale.msgs.DATE_TOO_FAR_IN_FUTURE;
        }
    }

    return "";
}

export function checkLanguageLevel(ll) {
    if(ll === LanguageLevel.INTERMEDIATE) {
        return false;
    }

    return true;
}

export function genId() {
    return String(Math.random()).replace(".", "");
}

export function showMatch(consumerContext) {
    return compareMediationStatus(consumerContext.mediationStatus, MediationStatus.MATCH_FOUND);
}

export function createNavigateSmartUrl(location, to) {
    const lang = new URLSearchParams(location.search).get('lang');

    if(!lang) {
        return to;
    }
    
    const hasQuery = to.includes('?');
    return `${to}${hasQuery ? '&' : '?'}lang=${lang}`;
}

// This method is useful when comparing e.g. the two dates strings "2025-03-22T17:19:00+00:00" and "2025-03-22T17:19:00.000Z"
// Returns true if the dates are equal
export function compareDateStrings(dateStr1, dateStr2) {
    if((dateStr1 === null && dateStr2 === "") || (dateStr2 === null && dateStr2 === "")) {
        return true;
    }

    return new Date(dateStr1).getTime() === new Date(dateStr2).getTime()
}

export function checkUtfString(str) {
    return [...str].every(char => {
        return char.codePointAt(0) <= 0xFFFF;
    });
}

export function checkUrl(urlStr) {
    try {
        new URL(urlStr);
        return true;
    } catch (error) {
        return false;
    }
}

export function getComplBasePath(basePath) {
    if(basePath === "/families") {
        return "/nannies";
    }
    else if(basePath === "/nannies") {
        return "/families";
    }
    else {
        return basePath;
    }
}

export function getPrettyStrFromUnderString(underStr) {
    let result = "";
    let lastC;

    for(let i = 0; i < underStr.length; i++) {
        const c = underStr[i];

        if(i === 0) {
            result += c.toUpperCase();
        }
        else if(c === "_") {
            result += "-";
        }
        else if(lastC === "_") {
            result += c.toUpperCase();
        }
        else {
            result += c;
        }

        lastC = c;
    }

    return result;
}

export function modifyAElesManagement(html) {
    return html.replaceAll("<a", "<a target=\"_blank\"");
}

export function modifyAElesCustomer(html) {
    return html.replaceAll("<a", "<a target=\"_blank\" class=\"mod_a\"");
}

export function getCustomerHostnameExtended() {
    if(system === "development") {
        return "http://" + window.location.hostname + ":3000";
    }
    else if(system === "staging") {
        return "https://" + window.location.hostname.replace("management.", "");
    }
    else if(system === "production") {
        return "https://" + window.location.hostname.replace("management.", "");
    }
}

export function getManagementLogoPath(userLocation) {
    return (
        userLocation === Location.NUREMBERG ?
            "/management/Logo_plain_2_inverted_nuremberg.svg"
        : userLocation === Location.MUNICH ?
            "/management/Logo_plain_2_inverted_munich.svg"
        : "/management/Logo_plain_2_inverted.svg"
    );
}

export function getTitleStr(title) {
    return title + (system === "staging" ? " (Staging)" : "");
}

export function getDefaultTitle(locale) {
    return locale.msgs.SITE_TITLE_INDEX;
}

export function getDisplayLanguage(locale) {
    if(locale.locale === "de") {
        return DisplayLanguage.GERMAN;
    }
    else if(locale.locale === "en") {
        return DisplayLanguage.ENGLISH;
    }
    else {
        console.error("Unsupported display language");
        return DisplayLanguage.ENGLISH;
    }
}

export function canMatchBeCancelled(user) {
    return !compareMatchStatus(user.match.status, MatchStatus.DONE);
}

export function hasMatchBeenCancelled(user1, user2) {
    return user1.match_cancelled || user2.match_cancelled;
}

export function isMatchCreationPossible(user1, user2) {
    return !hasMatchBeenCancelled(user1, user2) &&  // The users must not be matched before
            !user1.match && !user2.match &&  // None of the users can be in a match
            user1.mediation_status === MediationStatus.FIND_MATCH && user2.mediation_status === MediationStatus.FIND_MATCH &&
            user1.earmarked_matches.length === 0 && user2.earmarked_matches.length === 0; // No earmarked matches should be present
}

export function isEarmarkPossible(user1, user2) {
    return !hasMatchBeenCancelled(user1, user2) &&  // The users must not be matched before
            !user1.match && !user2.match &&  // None of the users can be in a match
            user1.earmarked_matches.length === 0 && user2.earmarked_matches.length === 0 && // No earmarked matches should be present
            !user1.declined && !user2.declined;
}

export function isMatched(user1, user2) {
    return user1.match && user2.match && user1.match.id === user2.match.id;
}

export function isEarmarked(userWithEarmarkedList, otherUser) {
    for(const m of userWithEarmarkedList.earmarked_matches) {
        if(m.id === otherUser.id) {
            return true;
        }
    }

    return false;
}

export function getLocationTransListsForFilter(locale) {
    const [locationTransList, locationTransListInverse] = get_translation_objects("location", Location, locale);
    locationTransList[""] = locale.msgs.ALL_LOCATIONS;
    locationTransListInverse[locale.msgs.ALL_LOCATIONS] = "";

    return [locationTransList, locationTransListInverse];
}

export function getHost() {
    let host = "https://" + window.location.hostname;

    if(system === "development") {
        host = "http://" + window.location.hostname + ":8000";
    }

    return host;
}

export function getMinutesSecondsStr(locale, seconds) {
    seconds = Math.ceil(seconds);
    let minutes = Math.ceil(seconds / 60);

    if(minutes > 1) {
        return minutes + " " + locale.msgs.MINUTES;
    }
    else {  // minutes <= 1
        if(seconds > 1) {
            return seconds + " " + locale.msgs.SECONDS;
        }
        else if(seconds === 1) {
            return seconds + " " + locale.msgs.SECOND;
        }
        else {  // seconds <= 0
            return locale.msgs.NOW;
        }
    }
}

export function getInMinutesSecondsStr(locale, seconds) {
    let str = getMinutesSecondsStr(locale, seconds);

    if(str === locale.msgs.NOW) {
        return str;
    }
    else {
        return locale.msgs.IN + " " + str;
    }
}
